/**
 * Staffing engine (pure functions, no I/O)
 *
 * Two stages, both deterministic and side-effect free so they can be unit
 * tested and a constraint solver could later replace the greedy assigner behind
 * the same interface:
 *
 *  1. buildShiftPlan — turn per-happening role requirements into concrete shift
 *     slots per (stage, role). Overlapping requirements on a stage collapse to
 *     the max concurrent count; coverage is sliced into target-length shifts.
 *  2. assignShiftsGreedy — fill shifts from an eligible pool, respecting role
 *     qualification, availability, and per-person consecutive/daily-hour caps,
 *     distributing work equitably (least-loaded first). Locked assignments are
 *     treated as fixed and count toward a person's load.
 */

const MINUTE = 60_000;
const HOUR = 3_600_000;

// Shift boundaries snap to this grid so we get clean times (no 23:52 artifacts).
const SNAP_MS = 15 * MINUTE;
// Coverage segments shorter than this don't get their own shift — they're
// smoothed into neighbours, so a brief overlap between two happenings won't
// spawn a 10-minute shift for a second person.
const MIN_SEGMENT_MS = 30 * MINUTE;

const roundTo = (ms: number, grid: number) => Math.round(ms / grid) * grid;

// ---------------------------------------------------------------------------
// Stage 1: shift planning
// ---------------------------------------------------------------------------

export interface PlanHappening {
  id: string;
  place_id: string | null;
  start_time: string | null;
  end_time: string | null;
}

export interface ShiftSpec {
  place_id: string;
  role_id: string;
  start: string; // ISO
  end: string; // ISO
}

export interface BuildShiftPlanInput {
  happenings: PlanHappening[];
  /** role requirements keyed by happening id */
  requirements: Record<string, Array<{ role_id: string; count: number }>>;
  /** Shortest a shift should be; coverage runs are balanced to keep shifts at or above this. */
  minShiftMinutes: number;
  /** Longest a shift may be; runs are split so no shift exceeds this. */
  maxShiftMinutes: number;
  /** Minutes of coverage to add before the first / after the last act of a run. */
  leadInMinutes?: number;
  leadOutMinutes?: number;
}

interface Segment {
  start: number;
  end: number;
  concurrency: number;
}

/**
 * Sweep a set of {start,end,count} intervals into piecewise-constant segments
 * where concurrency is the max count among overlapping intervals. Adjacent
 * segments with equal concurrency are merged. Zero-concurrency gaps are dropped.
 */
function coverageSegments(intervals: Array<{ start: number; end: number; count: number }>): Segment[] {
  const valid = intervals.filter((i) => i.end > i.start && i.count > 0);
  if (valid.length === 0) return [];

  const points = Array.from(new Set(valid.flatMap((i) => [i.start, i.end]))).sort((a, b) => a - b);
  const raw: Segment[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];
    if (end <= start) continue;
    const mid = (start + end) / 2;
    let concurrency = 0;
    for (const iv of valid) {
      if (iv.start <= mid && mid < iv.end) concurrency = Math.max(concurrency, iv.count);
    }
    if (concurrency > 0) raw.push({ start, end, concurrency });
  }

  const mergeAdjacent = (segs: Segment[]): Segment[] => {
    const out: Segment[] = [];
    for (const seg of segs) {
      const last = out[out.length - 1];
      if (last && last.end === seg.start && last.concurrency === seg.concurrency) last.end = seg.end;
      else out.push({ ...seg });
    }
    return out;
  };

  let merged = mergeAdjacent(raw);

  // Smooth out sub-threshold segments: a brief concurrency spike (e.g. a 2-min
  // overlap during a changeover) is flattened to its neighbours' level rather
  // than spawning a tiny extra shift. A lone short window is kept (it still
  // needs staffing).
  for (;;) {
    if (merged.length < 2) break;
    let idx = -1;
    let shortest = MIN_SEGMENT_MS;
    for (let i = 0; i < merged.length; i++) {
      const dur = merged[i].end - merged[i].start;
      if (dur < shortest) {
        shortest = dur;
        idx = i;
      }
    }
    if (idx === -1) break;
    const prev = merged[idx - 1];
    const next = merged[idx + 1];
    // Flatten toward the lower neighbouring concurrency (drops brief spikes,
    // fills brief dips up to the smaller neighbour).
    if (prev && next) merged[idx].concurrency = Math.min(prev.concurrency, next.concurrency);
    else merged[idx].concurrency = (prev ?? next).concurrency;
    merged = mergeAdjacent(merged);
  }

  return merged;
}

/**
 * Slice [start,end] into shifts whose length sits within [minMs, maxMs].
 *
 * Keeping shifts inside the band is the priority; lining cuts up with event
 * boundaries is a nicety, not a requirement. So we first pick how many balanced
 * pieces to make, then for each cut we only snap to an event boundary if that
 * boundary keeps the piece within the band — otherwise we cut on the grid near
 * the even-split point (mid-performance is fine). This stops a stray boundary
 * just below the minimum from producing a too-short shift.
 *
 * When a run is shorter than maxMs it stays a single shift even if that is below
 * minMs, since the coverage is still genuinely needed.
 */
function sliceWindow(
  start: number,
  end: number,
  minMs: number,
  maxMs: number,
  cuts: number[],
): Array<{ start: number; end: number }> {
  if (end <= start) return [];
  const L = end - start;

  // Choose the piece count. Aim for the band midpoint as the average, but keep
  // every piece within [min, max] whenever the run length allows it:
  //   nLow  = fewest pieces that keep each ≤ max
  //   nHigh = most pieces that keep each ≥ min
  const avg = (minMs + maxMs) / 2;
  const nLow = Math.max(1, Math.ceil(L / maxMs));
  const nHigh = Math.max(1, Math.floor(L / minMs));
  let n: number;
  if (nLow <= nHigh) {
    n = Math.min(nHigh, Math.max(nLow, Math.round(L / avg)));
  } else {
    // Run can't be tiled within the band (its length falls between max and
    // 2·min). Pick whichever bound lands closest to the band edge.
    const overMax = L / nHigh - maxMs;
    const underMin = minMs - L / nLow;
    n = overMax <= underMin ? nHigh : nLow;
  }
  if (n <= 1) return [{ start, end }];

  const boundaries = Array.from(new Set(cuts.filter((c) => c > start && c < end))).sort((a, b) => a - b);
  const idealLen = L / n;

  const out: Array<{ start: number; end: number }> = [];
  let cur = start;
  for (let k = 1; k < n; k++) {
    const ideal = cur + idealLen;
    const remaining = n - k;
    // Window that keeps this piece AND the remainder within the band.
    const lo = Math.max(cur + minMs, end - remaining * maxMs);
    const hi = Math.min(cur + maxMs, end - remaining * minMs);
    let next: number;
    if (lo <= hi) {
      // Snap to an act boundary only if one sits inside the band; otherwise cut
      // on the grid near the even split — the band matters more than the boundary.
      const inRange = boundaries.filter((b) => b >= lo && b <= hi);
      next =
        inRange.length > 0
          ? inRange.reduce((best, b) => (Math.abs(b - ideal) < Math.abs(best - ideal) ? b : best), inRange[0])
          : Math.min(Math.max(roundTo(ideal, SNAP_MS), lo), hi);
    } else {
      // No band-respecting cut exists (squeeze); keep moving on the grid.
      const lb = Math.max(cur + SNAP_MS, end - remaining * maxMs);
      const ub = Math.max(lb, Math.min(cur + maxMs, end - remaining * SNAP_MS));
      next = Math.min(Math.max(roundTo(ideal, SNAP_MS), lb), ub);
    }
    out.push({ start: cur, end: next });
    cur = next;
  }
  out.push({ start: cur, end });
  return out;
}

export function buildShiftPlan(input: BuildShiftPlanInput): ShiftSpec[] {
  const minMs = Math.max(1, input.minShiftMinutes) * MINUTE;
  const maxMs = Math.max(minMs, Math.max(1, input.maxShiftMinutes) * MINUTE);
  const leadInMs = Math.max(0, input.leadInMinutes ?? 0) * MINUTE;
  const leadOutMs = Math.max(0, input.leadOutMinutes ?? 0) * MINUTE;

  // Gather requirement intervals grouped by (place, role).
  const byPlaceRole = new Map<string, Array<{ start: number; end: number; count: number }>>();
  for (const h of input.happenings) {
    if (!h.place_id || !h.start_time || !h.end_time) continue;
    const rawStart = Date.parse(h.start_time);
    const rawEnd = Date.parse(h.end_time);
    if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd) || rawEnd <= rawStart) continue;
    // Round to the nearest minute to strip sub-minute noise in stored timestamps
    // (events can carry stray seconds/ms, e.g. 22:00:28.124), so boundaries are
    // clean and adjacent events meet exactly. This is intentionally fine-grained
    // (1 min) — it does not coarsen real event times.
    const start = roundTo(rawStart, MINUTE);
    let end = roundTo(rawEnd, MINUTE);
    if (end <= start) end = start + MINUTE;
    for (const req of input.requirements[h.id] ?? []) {
      if (req.count <= 0) continue;
      const key = `${h.place_id}|${req.role_id}`;
      (byPlaceRole.get(key) ?? byPlaceRole.set(key, []).get(key)!).push({ start, end, count: req.count });
    }
  }

  const shifts: ShiftSpec[] = [];
  for (const [key, intervals] of byPlaceRole) {
    const [place_id, role_id] = key.split('|');
    // Event boundaries (snapped) — preferred shift cut points so hand-overs
    // happen between performances, not in the middle of one.
    const eventBoundaries = Array.from(new Set(intervals.flatMap((i) => [i.start, i.end])));
    const segments = coverageSegments(intervals);
    // Lead-in/out: extend the outer edges of each contiguous coverage run, so a
    // person is on shift before the first act / after the last (call-time,
    // teardown) without altering internal boundaries or concurrency.
    if (leadInMs > 0 || leadOutMs > 0) {
      for (let i = 0; i < segments.length; i++) {
        const isRunStart = i === 0 || segments[i - 1].end !== segments[i].start;
        const isRunEnd = i === segments.length - 1 || segments[i + 1].start !== segments[i].end;
        if (isRunStart) segments[i].start -= leadInMs;
        if (isRunEnd) segments[i].end += leadOutMs;
      }
    }
    for (const seg of segments) {
      const slices = sliceWindow(seg.start, seg.end, minMs, maxMs, eventBoundaries);
      for (let track = 0; track < seg.concurrency; track++) {
        for (const s of slices) {
          shifts.push({
            place_id,
            role_id,
            start: new Date(s.start).toISOString(),
            end: new Date(s.end).toISOString(),
          });
        }
      }
    }
  }

  shifts.sort(
    (a, b) =>
      a.place_id.localeCompare(b.place_id) ||
      a.role_id.localeCompare(b.role_id) ||
      a.start.localeCompare(b.start),
  );
  return shifts;
}

// ---------------------------------------------------------------------------
// Stage 2: greedy assignment
// ---------------------------------------------------------------------------

export interface EnginePerson {
  id: string;
  roleIds: string[];
  availability: Array<{ start: string; end: string }>;
  maxConsecutiveHours?: number | null;
  maxDailyHours?: number | null;
  /** If non-empty, this person may only be assigned to shifts at these places. */
  allowedPlaceIds?: string[] | null;
}

export interface EngineShift {
  id: string;
  place_id: string;
  role_id: string;
  start: string;
  end: string;
}

export interface ExistingAssignment {
  shiftId: string;
  personId: string;
  locked: boolean;
}

export interface AssignOptions {
  /** Gap (minutes) up to which two of a person's shifts count as one continuous
   *  stretch for the consecutive-hours cap. Continuity-preferred default. */
  continuityGapMinutes?: number;
  /** Hard cap on how many shifts any one person may be assigned. */
  maxShiftsPerPerson?: number;
}

export interface AssignResult {
  /** Newly proposed assignments (excludes pre-existing locked ones). */
  assignments: Array<{ shiftId: string; personId: string }>;
  unfilled: string[];
  warnings: Array<{ shiftId: string; message: string }>;
}

interface Interval {
  start: number;
  end: number;
}

function parseInterval(s: { start: string; end: string }): Interval {
  return { start: Date.parse(s.start), end: Date.parse(s.end) };
}

function mergeIntervals(intervals: Interval[]): Interval[] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const out: Interval[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const last = out[out.length - 1];
    if (sorted[i].start <= last.end) last.end = Math.max(last.end, sorted[i].end);
    else out.push({ ...sorted[i] });
  }
  return out;
}

// The engine runs in the same (browser) timezone availability was entered in,
// so local-time checks are consistent.
function isLocalMidnight(epoch: number): boolean {
  const d = new Date(epoch);
  return d.getHours() === 0 && d.getMinutes() === 0;
}

const DAY_MS = 24 * HOUR;

/**
 * Whether a person's availability covers a shift.
 *
 * Normal case: a (merged) window fully contains the shift. Festival carryover:
 * if a shift starts inside an all-day window and spills past midnight, allow it
 * — *unless* the person recorded any availability starting on that next day
 * (which means they deliberately scoped it). This lets "free Friday" cover a
 * Friday-night-into-Saturday-morning block without extra data entry, while still
 * respecting someone who said "Saturday I'm only free in the morning".
 */
function availabilityCovers(windows: Interval[], shift: Interval): boolean {
  const merged = mergeIntervals(windows);
  if (merged.some((w) => w.start <= shift.start && w.end >= shift.end)) return true;

  for (const w of merged) {
    if (w.start <= shift.start && shift.start < w.end && shift.end > w.end && isLocalMidnight(w.end)) {
      const hasNextDayInfo = windows.some((x) => x.start >= w.end && x.start < w.end + DAY_MS);
      if (!hasNextDayInfo) return true;
    }
  }
  return false;
}

function sameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getUTCFullYear() === db.getUTCFullYear() &&
    da.getUTCMonth() === db.getUTCMonth() &&
    da.getUTCDate() === db.getUTCDate()
  );
}

/** Longest continuous stretch (ms) covering `candidate`, chaining shifts whose
 *  gap is within continuityGap. */
function maxStretchMs(assigned: Interval[], candidate: Interval, continuityGapMs: number): number {
  const all = [...assigned, candidate].sort((a, b) => a.start - b.start);
  let bestStart = all[0].start;
  let bestEnd = all[0].end;
  let curStart = all[0].start;
  let curEnd = all[0].end;
  for (let i = 1; i < all.length; i++) {
    const iv = all[i];
    if (iv.start - curEnd <= continuityGapMs) {
      curEnd = Math.max(curEnd, iv.end);
    } else {
      curStart = iv.start;
      curEnd = iv.end;
    }
    if (curEnd - curStart > bestEnd - bestStart) {
      bestStart = curStart;
      bestEnd = curEnd;
    }
  }
  return bestEnd - bestStart;
}

function dailyMs(assigned: Interval[], candidate: Interval): number {
  let total = candidate.end - candidate.start;
  for (const iv of assigned) {
    if (sameDay(iv.start, candidate.start)) total += iv.end - iv.start;
  }
  return total;
}

export function assignShiftsGreedy(
  shifts: EngineShift[],
  people: EnginePerson[],
  existing: ExistingAssignment[],
  options: AssignOptions = {},
): AssignResult {
  const continuityGapMs = (options.continuityGapMinutes ?? 30) * MINUTE;

  const lockedByShift = new Map<string, string>();
  for (const a of existing) if (a.locked) lockedByShift.set(a.shiftId, a.personId);

  // Per-person load (assigned intervals + total ms), seeded from locked assignments.
  const shiftById = new Map(shifts.map((s) => [s.id, s]));
  const load = new Map<string, { intervals: Interval[]; totalMs: number }>();
  const ensure = (pid: string) => {
    let l = load.get(pid);
    if (!l) load.set(pid, (l = { intervals: [], totalMs: 0 }));
    return l;
  };
  for (const a of existing) {
    if (!a.locked) continue;
    const s = shiftById.get(a.shiftId);
    if (!s) continue;
    const iv = parseInterval(s);
    const l = ensure(a.personId);
    l.intervals.push(iv);
    l.totalMs += iv.end - iv.start;
  }

  const peopleByRole = new Map<string, EnginePerson[]>();
  for (const p of people) {
    for (const r of p.roleIds) {
      (peopleByRole.get(r) ?? peopleByRole.set(r, []).get(r)!).push(p);
    }
  }

  const availabilityCache = new Map<string, Interval[]>();
  const avail = (p: EnginePerson) => {
    let a = availabilityCache.get(p.id);
    if (!a) availabilityCache.set(p.id, (a = p.availability.map(parseInterval)));
    return a;
  };

  // Open shifts = those without a locked assignment. Assign earliest-first; for
  // ties, the shift with the fewest eligible people first (hardest to fill).
  const open = shifts.filter((s) => !lockedByShift.has(s.id));
  const eligibleCount = (s: EngineShift): number => {
    const iv = parseInterval(s);
    return (peopleByRole.get(s.role_id) ?? []).filter((p) => availabilityCovers(avail(p), iv)).length;
  };
  open.sort(
    (a, b) =>
      a.start.localeCompare(b.start) ||
      eligibleCount(a) - eligibleCount(b) ||
      a.id.localeCompare(b.id),
  );

  const assignments: Array<{ shiftId: string; personId: string }> = [];
  const unfilled: string[] = [];
  const warnings: Array<{ shiftId: string; message: string }> = [];

  for (const shift of open) {
    const iv = parseInterval(shift);
    const candidates = (peopleByRole.get(shift.role_id) ?? []).filter((p) => {
      // No double-booking: candidate must be free during the shift.
      const l = ensure(p.id);
      if (l.intervals.some((x) => x.start < iv.end && iv.start < x.end)) return false;
      // Max-shifts cap: intervals.length is this person's current shift count.
      if (options.maxShiftsPerPerson != null && l.intervals.length >= options.maxShiftsPerPerson) {
        return false;
      }
      // Location restriction: only assignable to their allowed places.
      if (p.allowedPlaceIds && p.allowedPlaceIds.length > 0 && !p.allowedPlaceIds.includes(shift.place_id)) {
        return false;
      }
      return availabilityCovers(avail(p), iv);
    });

    if (candidates.length === 0) {
      unfilled.push(shift.id);
      continue;
    }

    // Rank: respect hour caps as hard filters where possible; least-loaded wins.
    const scored = candidates.map((p) => {
      const l = ensure(p.id);
      const stretchMs = maxStretchMs(l.intervals, iv, continuityGapMs);
      const dMs = dailyMs(l.intervals, iv);
      const exceedsConsecutive =
        p.maxConsecutiveHours != null && stretchMs > p.maxConsecutiveHours * HOUR;
      const exceedsDaily = p.maxDailyHours != null && dMs > p.maxDailyHours * HOUR;
      return { p, l, exceedsConsecutive, exceedsDaily };
    });

    const within = scored.filter((s) => !s.exceedsConsecutive && !s.exceedsDaily);
    const pool = within.length > 0 ? within : scored;
    pool.sort((a, b) => a.l.totalMs - b.l.totalMs || a.p.id.localeCompare(b.p.id));
    const chosen = pool[0];

    chosen.l.intervals.push(iv);
    chosen.l.totalMs += iv.end - iv.start;
    assignments.push({ shiftId: shift.id, personId: chosen.p.id });

    if (within.length === 0) {
      const reasons: string[] = [];
      if (chosen.exceedsConsecutive) reasons.push('exceeds max consecutive hours');
      if (chosen.exceedsDaily) reasons.push('exceeds max daily hours');
      warnings.push({
        shiftId: shift.id,
        message: `Assigned ${chosen.p.id} despite: ${reasons.join(', ')} (no conflict-free option).`,
      });
    }
  }

  return { assignments, unfilled, warnings };
}
